import React, { FC, useEffect, useState } from 'react';
import { Box, Typography, Switch, CircularProgress, Paper, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { SCUIGeneralDrawer } from '../../../atomic/molecules/SCUIGeneralDrawer/SCUIGeneralDrawer';
import { SCUIButton } from '../../../atomic/atoms/SCUIButton/SCUIButton';
import { FDCM } from '../fastDeliveryCargoManagement.classes';

interface EditDeliveryDrawerProps {
    open: boolean;
    onClose: () => void;
    delivery: {
        id: number;
        shippingCompanyId: number;
        shippingCompanyName: string;
        cityName: string;
        countyName: string;
        neighborhoodName: string;
        isActiveForTomorrowDelivery: boolean;
        isActiveForExpressDelivery: boolean;
        isActiveForTodayDelivery: boolean;
        addressEntityId: number;
    } | null;
    onSave: (data: {
        shippingCompanyId: number;
        addressEntityId: number;
        isActiveForTomorrowDelivery: boolean;
        isActiveForExpressDelivery: boolean;
        isActiveForTodayDelivery: boolean;
    }) => Promise<void>;
    loading: boolean;
}

const EditDeliveryDrawer: FC<EditDeliveryDrawerProps> = ({ open, onClose, delivery, onSave, loading }) => {
    const { t } = useTranslation();
    const [form, setForm] = useState({
        isActiveForTomorrowDelivery: delivery?.isActiveForTomorrowDelivery || false,
        isActiveForExpressDelivery: delivery?.isActiveForExpressDelivery || false,
        isActiveForTodayDelivery: delivery?.isActiveForTodayDelivery || false,
    });

    useEffect(() => {
        if (delivery) {
            setForm({
                isActiveForTomorrowDelivery: delivery.isActiveForTomorrowDelivery,
                isActiveForExpressDelivery: delivery.isActiveForExpressDelivery,
                isActiveForTodayDelivery: delivery.isActiveForTodayDelivery,
            });
        }
    }, [delivery]);

    const handleClose = () => {
        setForm({
            isActiveForTomorrowDelivery: false,
            isActiveForExpressDelivery: false,
            isActiveForTodayDelivery: false,
        });
        onClose();
    };

    const handleSwitch = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm((prev) => ({ ...prev, [key]: e.target.checked }));
    };

    const handleSave = async () => {
        if (!delivery) return;

        await onSave({
            shippingCompanyId: delivery.shippingCompanyId,
            addressEntityId: delivery.addressEntityId,
            ...form,
        });
    };

    return (
        <SCUIGeneralDrawer open={open} onClose={handleClose} width={400}>
            <Box
                className={FDCM.editDrawerRoot}
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: '#fafafa',
                }}
            >
                <Box
                    className={FDCM.editDrawerHeader}
                    sx={{
                        p: 3,
                        borderBottom: '1px solid #e0e0e0',
                        bgcolor: 'white',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {t('fastDeliveryCargoManagement.edit')}
                    </Typography>
                    <IconButton className={FDCM.inputButtonCloseEditDrawer} onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box
                    className={FDCM.editDrawerBody}
                    sx={{
                        flex: 1,
                        overflow: 'auto',
                        p: 3,
                    }}
                >
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2.5,
                            mb: 3,
                            bgcolor: 'white',
                            borderRadius: 2,
                            border: '1px solid #e0e0e0',
                        }}
                    >
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            {t('fastDeliveryCargoManagement.shippingCompany')}
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                fontWeight: 500,
                                color: delivery?.shippingCompanyName === 'Hepsijet' ? '#1976d2' : '#388e3c',
                                bgcolor: delivery?.shippingCompanyName === 'Hepsijet' ? '#e3f0ff' : '#e6f4ea',
                                p: 1,
                                borderRadius: 1,
                                display: 'inline-block',
                            }}
                        >
                            {delivery?.shippingCompanyName}
                        </Typography>
                    </Paper>

                    <Paper
                        elevation={0}
                        sx={{
                            p: 2.5,
                            mb: 3,
                            bgcolor: 'white',
                            borderRadius: 2,
                            border: '1px solid #e0e0e0',
                        }}
                    >
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            {t('fastDeliveryCargoManagement.address')}
                        </Typography>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                                {t('fastDeliveryCargoManagement.city')}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {delivery?.cityName}
                            </Typography>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                                {t('fastDeliveryCargoManagement.district')}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {delivery?.countyName}
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="caption" color="text.secondary" display="block">
                                {t('fastDeliveryCargoManagement.neighborhood')}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {delivery?.neighborhoodName}
                            </Typography>
                        </Box>
                    </Paper>

                    <Paper
                        elevation={0}
                        sx={{
                            p: 2.5,
                            bgcolor: 'white',
                            borderRadius: 2,
                            border: '1px solid #e0e0e0',
                        }}
                    >
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            {t('fastDeliveryCargoManagement.deliverySettings')}
                        </Typography>

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 1.5,
                                borderRadius: 1,
                                '&:hover': {
                                    bgcolor: '#f5f5f5',
                                },
                            }}
                        >
                            <Typography variant="body2">{t('fastDeliveryCargoManagement.tomorrowDelivery')}</Typography>
                            <Switch
                                className={FDCM.inputSwitchEditTomorrowDelivery}
                                checked={form.isActiveForTomorrowDelivery}
                                onChange={handleSwitch('isActiveForTomorrowDelivery')}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: '#00B8F4',
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: '#00B8F4',
                                    },
                                }}
                            />
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 1.5,
                                borderRadius: 1,
                                '&:hover': {
                                    bgcolor: '#f5f5f5',
                                },
                            }}
                        >
                            <Typography variant="body2">{t('fastDeliveryCargoManagement.expressDelivery')}</Typography>
                            <Switch
                                className={FDCM.inputSwitchEditExpressDelivery}
                                checked={form.isActiveForExpressDelivery}
                                onChange={handleSwitch('isActiveForExpressDelivery')}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: '#00B8F4',
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: '#00B8F4',
                                    },
                                }}
                            />
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 1.5,
                                borderRadius: 1,
                                '&:hover': {
                                    bgcolor: '#f5f5f5',
                                },
                            }}
                        >
                            <Typography variant="body2">{t('fastDeliveryCargoManagement.todayDelivery')}</Typography>
                            <Switch
                                className={FDCM.inputSwitchEditTodayDelivery}
                                checked={form.isActiveForTodayDelivery}
                                onChange={handleSwitch('isActiveForTodayDelivery')}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: '#00B8F4',
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: '#00B8F4',
                                    },
                                }}
                            />
                        </Box>
                    </Paper>
                </Box>

                <Box
                    className={FDCM.editDrawerFooter}
                    sx={{
                        p: 2,
                        borderTop: '1px solid #e0e0e0',
                        bgcolor: 'white',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 2,
                    }}
                >
                    <SCUIButton
                        className={FDCM.inputButtonEditCancel}
                        onClick={handleClose}
                        type="secondary"
                        sizeType="default"
                        size="m"
                        text={t('general.cancel')}
                        disabled={loading}
                    />
                    <SCUIButton
                        className={FDCM.inputButtonEditSave}
                        onClick={handleSave}
                        type="primary"
                        sizeType="default"
                        size="m"
                        text={t('general.save')}
                        disabled={loading}
                    >
                        {loading && <CircularProgress size={20} color="inherit" sx={{ ml: 1 }} />}
                    </SCUIButton>
                </Box>
            </Box>
        </SCUIGeneralDrawer>
    );
};

export default EditDeliveryDrawer;
